import { type KeyboardEvent } from 'react';

import { type IResultAnalyticsData } from '../../../../../interface/state/IElectionState';
import { CheckSVG, CopySVG } from '../../../../utils/svgs';
import { CoverageChip, StatusBadge } from '../components';
import { getUploadedLabel, puStatus, type RowCoverage } from '../utils';
import Pager from './Pager';

interface ITableViewProps {
	rows: IResultAnalyticsData[];
	copiedCode: string;
	copySuccess: boolean;
	onCopy: (value: string, key: string) => void;
	onSelectChild: (item: IResultAnalyticsData) => void;
	coverage: Map<string, RowCoverage>;
	coverageLoading: boolean;
	currentPage: number;
	totalPages: number;
	onPage: (page: number) => void;
	hasActiveFilter: boolean;
	entityLabel: string;
}

/** Dense, scan-friendly table of the same child rows. Row click opens coverage. */
const TableView = ({
	rows,
	copiedCode,
	copySuccess,
	onCopy,
	onSelectChild,
	coverage,
	coverageLoading,
	currentPage,
	totalPages,
	onPage,
	hasActiveFilter,
	entityLabel,
}: ITableViewProps) => {
	if (!rows.length) {
		return (
			<div className='qa-empty-filter'>
				<p>{hasActiveFilter ? 'No results match your filters.' : 'No results to show.'}</p>
			</div>
		);
	}

	const activate = (item: IResultAnalyticsData) => onSelectChild(item);

	return (
		<>
			<div className='qa-table-wrap'>
				<table className='qa-table'>
					<thead>
						<tr>
							<th scope='col'>{entityLabel.replace(/s$/, '')} code</th>
							<th scope='col'>Agent</th>
							<th scope='col'>Status</th>
							<th scope='col'>Uploaded</th>
							<th
								scope='col'
								className='qa-table__actions-col'
								aria-label='Actions'
							/>
						</tr>
					</thead>
					<tbody>
						{rows.map((item) => {
							const uploadedLabel = getUploadedLabel(item);
							const isCopied = copySuccess && copiedCode === item.code;
							return (
								<tr
									key={item.id}
									className='qa-table__row'
									role='button'
									tabIndex={0}
									aria-label={`View agent for ${item.code}`}
									onClick={() => activate(item)}
									onKeyDown={(e: KeyboardEvent) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											activate(item);
										}
									}}
								>
									<td className='qa-table__code'>{item.code}</td>
									<td>
										<CoverageChip
											coverage={coverage.get(item.code)}
											loading={coverageLoading}
										/>
									</td>
									<td>
										<StatusBadge status={puStatus(item)} />
									</td>
									<td className='qa-table__meta'>{uploadedLabel ?? <span className='qa-table__muted'>Awaiting upload</span>}</td>
									<td className='qa-table__actions'>
										<button
											type='button'
											className={`qa-row__copy ${isCopied ? 'qa-row__copy--done' : ''}`}
											onClick={(e) => {
												e.stopPropagation();
												onCopy(item.code, item.code);
											}}
											aria-label={`Copy code ${item.code}`}
										>
											{isCopied ? (
												<CheckSVG
													width='16'
													height='16'
													color='currentColor'
												/>
											) : (
												<CopySVG />
											)}
										</button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<Pager
				currentPage={currentPage}
				totalPages={totalPages}
				onPage={onPage}
			/>
		</>
	);
};

export default TableView;
